import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, X, ArrowRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import {
  OtpEventType,
  OtpStatus,
  OtpChannel,
} from "@/pages/otp-log/types/otp-log";

export default function OtpFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // const eventTypeOptions = [
  //   { value: "ALL", label: "All Events" },
  //   { value: OtpEventType.OTP_SENT, label: "OTP Sent" },
  //   { value: OtpEventType.OTP_DELIVERED, label: "OTP Delivered" },
  //   { value: OtpEventType.OTP_VERIFIED, label: "OTP Verified" },
  //   { value: OtpEventType.OTP_FAILED, label: "OTP Failed" },
  //   { value: OtpEventType.OTP_EXPIRED, label: "OTP Expired" },
  // ];

  const statusOptions = [
    { value: "ALL", label: "All Status" },
    { value: OtpStatus.SUCCESS, label: "Success" },
    { value: OtpStatus.FAILED, label: "Failed" },
    { value: OtpStatus.PENDING, label: "Pending" },
  ];

  const channelOptions = [
    { value: "ALL", label: "All Channels" },
    { value: OtpChannel.EMAIL, label: "Email" },
    { value: OtpChannel.WHATSAPP, label: "WhatsApp" },
  ];

  const currentEventType = searchParams.get("event_type") || "ALL";
  const currentStatus = searchParams.get("status") || "ALL";
  const currentChannel = searchParams.get("channel") || "ALL";

  const handleEventTypeChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    if (value === "ALL") {
      newSearchParams.delete("event_type");
    } else {
      newSearchParams.set("event_type", value);
    }
    setSearchParams(newSearchParams);
  };

  const handleStatusChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    if (value === "ALL") {
      newSearchParams.delete("status");
    } else {
      newSearchParams.set("status", value);
    }
    setSearchParams(newSearchParams);
  };

  const handleChannelChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    if (value === "ALL") {
      newSearchParams.delete("channel");
    } else {
      newSearchParams.set("channel", value);
    }
    setSearchParams(newSearchParams);
  };

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: searchParams.get("startDate")
      ? new Date(searchParams.get("startDate")!)
      : null,
    end: searchParams.get("endDate")
      ? new Date(searchParams.get("endDate")!)
      : null,
  });

  const handleDateChange = (range?: { from?: Date; to?: Date }) => {
    setDateRange({ start: range?.from || null, end: range?.to || null });

    if (range?.from && range?.to) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", "0");
      newSearchParams.set("startDate", range.from.toISOString());
      const endDate = new Date(range.to);
      endDate.setHours(23, 59, 59, 999);
      newSearchParams.set("endDate", endDate.toISOString());
      setSearchParams(newSearchParams);
    } else {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", "0");
      newSearchParams.delete("startDate");
      newSearchParams.delete("endDate");
      setSearchParams(newSearchParams);
    }
  };

  const handleClearDateRange = () => {
    setDateRange({ start: null, end: null });
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    newSearchParams.delete("startDate");
    newSearchParams.delete("endDate");
    setSearchParams(newSearchParams);
  };

  return (
    <div className="flex gap-2 items-center flex-wrap">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-60 justify-start text-left font-normal ${
              !dateRange.start && "text-muted-foreground"
            }`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange.start && dateRange.end
              ? `${format(dateRange.start, "LLL dd, y")} - ${format(
                  dateRange.end,
                  "LLL dd, y"
                )}`
              : "Pick a date range"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="start">
          <div className="flex items-end gap-3 mb-4 px-4">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Start
              </label>
              <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                {dateRange.start ? (
                  format(dateRange.start, "LLL dd, y")
                ) : (
                  <span className="text-muted-foreground">Select</span>
                )}
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                End
              </label>
              <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                {dateRange.end ? (
                  format(dateRange.end, "LLL dd, y")
                ) : (
                  <span className="text-muted-foreground">Select</span>
                )}
              </div>
            </div>
            {(dateRange.start || dateRange.end) && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 mb-0 text-muted-foreground hover:text-foreground"
                onClick={handleClearDateRange}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Calendar
            initialFocus
            mode="range"
            selected={{
              from: dateRange.start || undefined,
              to: dateRange.end || undefined,
            }}
            onSelect={handleDateChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>

      {/* <Select value={currentEventType} onValueChange={handleEventTypeChange}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Filter by event" />
        </SelectTrigger>
        <SelectContent>
          {eventTypeOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select> */}

      <Select value={currentStatus} onValueChange={handleStatusChange}>
        <SelectTrigger className="w-32">
          <SelectValue placeholder="Filter by status" />
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={currentChannel} onValueChange={handleChannelChange}>
        <SelectTrigger className="w-36">
          <SelectValue placeholder="Filter by channel" />
        </SelectTrigger>
        <SelectContent>
          {channelOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

    </div>
  );
}

